import {
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
} from '@grafana/faro-web-sdk';
import type { Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

import { isProduction } from '../../../identity-utils/src/environment';
import { isDatadog } from '../../../identity-utils/src/isDatadog';

import { analyticsConsentGranted } from './datadogRum';

// Mirrors datadogRum.ts's shouldTrackToDatadog exactly, so the dual run
// compares Datadog and Faro on the same population.
const shouldTrackToFaro = isProduction;

// Shared by the app meta and the bundleId global below, which faro-core keys on
// the app name.
const appName = 'identity';

// Kept in step with `allowedTracingUrls` in datadogRum.ts.
const firstPartyUrls = [/https:\/\/(.*\.)?zapier(-staging)?\.com/];

// Mutual exclusion, both directions. Faro ignores its own collector by default,
// but knows nothing about Datadog's — so without this, Faro records every
// Datadog RUM beacon as a resource event and feeds it into the mutual-recording
// loop described in the fe-observability-setup skill's mutual-exclusion
// reference. The other direction (Datadog ignoring Faro) needs two separate
// knobs instead of one and lives in datadogRum.ts's excludedActivityUrls +
// beforeSend. FullStory and Sentry are the other continuous collectors on
// these pages, so their beacons stay out of Faro too.
const otherAgentUrls = [
  // Datadog's browser intake: site "datadoghq.com" (US1, used below) becomes
  // browser-intake-datadoghq.com; other sites (us5, eu) hyphenate the same way.
  /https:\/\/browser-intake-[a-z0-9-]+\.(com|eu)\//,
  /https:\/\/rs\.fullstory\.com\//,
  /https:\/\/o\d+\.ingest\.(?:us\.|de\.)?sentry\.io\//,
];

// Guards setFaroTrackingConsent against running before init (non-prod / missing
// collector URL / synthetic UA).
let faroInstance: Faro | undefined;

export function initializeGrafanaFaro() {
  // Don't initialize during SSR
  if (typeof window === 'undefined') {
    return;
  }

  const url = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
  if (!url) {
    // eslint-disable-next-line no-console
    console.info('Grafana Faro not initialized: missing collector URL.');
    return;
  }

  if (!shouldTrackToFaro) {
    // eslint-disable-next-line no-console
    console.info(
      'Grafana Faro not initialized: not in a production environment.'
    );
    return;
  }

  // isDatadog() detects the Datadog Synthetics UA marker (see identity-utils),
  // which is also the right signal to exclude synthetic/bot traffic from Faro.
  if (isDatadog()) {
    // eslint-disable-next-line no-console
    console.info('Grafana Faro not initialized: synthetic user agent.');
    return;
  }

  // Grafana matches a stack trace to an uploaded sourcemap on the bundleId, so
  // this must equal the --bundle-id that scripts/upload-sourcemaps.mjs uploads
  // under; both are the build's commit SHA. faro-core reads meta.app.bundleId
  // only from this global — the one the Faro bundler plugins inject — and
  // overwrites whatever the config passed, so setting it here is the only way to
  // report it. Turbopack cannot run those plugins, hence the manual assignment.
  if (process.env.NEXT_PUBLIC_COMMIT_SHA) {
    (globalThis as unknown as Record<string, string>)[
      `__faroBundleId_${appName}`
    ] = process.env.NEXT_PUBLIC_COMMIT_SHA;
  }

  // See https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/instrument/
  // for more information on what these individual parameters do.
  faroInstance = initializeFaro({
    url,
    app: {
      name: appName,
      environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
    },
    // Start held unless the visitor has already consented; the consent effect in
    // _app grants once OneTrust analytics consent resolves, mirroring
    // datadogRum.ts's trackingConsent. See setFaroTrackingConsent.
    paused: !analyticsConsentGranted(),
    // Faro takes a 0-1 fraction where Datadog RUM's sessionSampleRate takes a
    // 0-100 percentage; both are 10% here.
    sessionTracking: {
      samplingRate: 0.1,
    },
    ignoreUrls: otherAgentUrls,
    // ConsoleInstrumentation is a genuinely new capture surface versus Datadog
    // RUM (which does not forward console arguments): identity/IdentityProvider.tsx
    // and identity/bridge/refreshJWT.ts both do `console.error(msg, error)` on
    // session-fetch and JWT-refresh failures, and this is a login/MFA product.
    // Keep Faro's own defaults (debug/trace/log) disabled and add error, so
    // those raw error objects are never forwarded as Faro exceptions.
    consoleInstrumentation: {
      disabledLevels: [
        LogLevel.DEBUG,
        LogLevel.TRACE,
        LogLevel.LOG,
        LogLevel.ERROR,
      ],
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        // Unsampled sessions would otherwise still send `traceparent` with the
        // sampled flag 00, which parent-based backend samplers (OTel default,
        // ddtrace) obey, so the frontend rate would also cut backend traces.
        // With this set, unsampled sessions send no trace context and the
        // backend samples those requests itself. Needs faro-web-tracing >= 2.11.0.
        omitTraceContextForUnsampledSessions: true,
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: firstPartyUrls,
        },
      }),
    ],
  });
}

// Keep Faro collection aligned with the visitor's analytics (C0002) consent as
// it resolves or changes, mirroring setDatadogTrackingConsent. No-op until
// init has run. Faro's transports drop every signal while paused rather than
// buffering it, so pausing genuinely withholds data instead of queuing it.
export function setFaroTrackingConsent(analyticsAllowed: boolean) {
  if (!faroInstance) {
    return;
  }

  if (analyticsAllowed) {
    faroInstance.unpause();
  } else {
    faroInstance.pause();
  }
}
